package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.TabUnderline
import co.maytes.standupbot.dashboard.components.widgets.focusAfterRender
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.Position
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

enum class TeamTab(val label: String) {
    Overview("Overview"),
    Schedule("Schedule"),
    Members("Members"),
    Template("Template"),
}

@Composable
fun TeamDetailTabsBar(
    active: TeamTab,
    onSelect: (TeamTab) -> Unit,
) {
    val tabs = TeamTab.entries
    val activeIdx = tabs.indexOf(active).coerceAtLeast(0)
    val widthPct = FULL_PERCENT / tabs.size

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Relative)
            .styleModifier {
                property("border-bottom", "1px solid ${Palette.border.rgbString()}")
            },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().role("tablist"),
            horizontalArrangement = Arrangement.Start,
        ) {
            tabs.forEach { tab ->
                val isActive = tab == active
                Box(
                    modifier = Modifier
                        .width(widthPct.percent)
                        .padding(topBottom = Spacing.sm)
                        .cursor(Cursor.Pointer)
                        .pressable()
                        .role("tab")
                        .tabIndex(0)
                        .attrsModifier { attr("aria-selected", isActive.toString()) }
                        .onClick { onSelect(tab) }
                        .onKeyDown { event ->
                            if (event.key == "Enter" || event.key == " ") {
                                event.preventDefault()
                                onSelect(tab)
                                val tabElement = event.target as? HTMLElement
                                focusAfterRender { tabElement }
                            }
                        }
                        .styleModifier { property("text-align", "center") },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        text = tab.label,
                        modifier = Modifier
                            .fontSize(Typography.body.size)
                            .fontWeight(TAB_LABEL_WEIGHT)
                            .color(if (isActive) Palette.textPrimary else Palette.textSecondary)
                            .styleModifier { property("transition", Motion.transition(Motion.DUR_FAST, "color")) },
                    )
                }
            }
        }
        TabUnderline(tabCount = tabs.size, activeIndex = activeIdx)
    }
}

private const val FULL_PERCENT = 100.0
private const val TAB_LABEL_WEIGHT = 500

@Composable
fun rememberActiveTeamTab(initial: TeamTab = TeamTab.Overview): ActiveTabHolder {
    val holder = remember { ActiveTabHolder() }
    if (holder.value == null) holder.value = initial
    return holder
}

class ActiveTabHolder {
    var value: TeamTab? by mutableStateOf(null)
}
